import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faAdjust } from "@fortawesome/free-solid-svg-icons"
import { COLORS } from "../../../utilities/Constants"
import { DEP_STATES } from "../../../utilities/Addons"

// Ordres d'assistance (GE-57) — pièces communes de l'interface (maquette
// validée : docs/correlation-assistances/maquette-assistances.html).

// Rôle d'un ordre : pastille (dépannage orange, transport vert d'eau, frais gris).
const ROLES = {
	depannage: { label: "Dépannage", color: "#9A3412", bg: "#FFEDD5" },
	transport: { label: "Transport", color: "#0F766E", bg: "#CCFBF1" },
	frais: { label: "Frais", color: COLORS.textMuted, bg: COLORS.bg },
	autre: { label: "Autre", color: COLORS.textMuted, bg: COLORS.bg },
}

export const RoleChip = ({ role, label }) => {
	const r = ROLES[role] || ROLES.autre
	return (
		<span
			style={{
				fontSize: 11,
				fontWeight: 700,
				borderRadius: 99,
				padding: "1px 8px",
				color: r.color,
				backgroundColor: r.bg,
				whiteSpace: "nowrap",
			}}
		>
			{label || r.label}
		</span>
	)
}

// Badge d'état de mission (mêmes teintes que DepMissionCardV2).
const ETATS = {
	10: { color: "#B45309", bg: "#FEF3C7" },
	20: { color: "#166534", bg: "#DCFCE7" },
	23: { color: "#7C2D12", bg: "#FFEDD5" },
	25: { color: "#1D4ED8", bg: "#DBEAFE" },
	27: { color: "#6D28D9", bg: "#EDE9FE" },
	30: { color: "#166534", bg: "#DCFCE7" },
	40: { color: "#166534", bg: "#DCFCE7" },
	50: { color: "#0E7490", bg: "#CFFAFE" },
	80: { color: COLORS.danger, bg: COLORS.dangerLight },
}

export const EtatBadge = ({ etat, label }) => {
	const e = ETATS[etat] || { color: COLORS.textMuted, bg: COLORS.bg }
	return (
		<span
			style={{
				padding: "2px 10px",
				borderRadius: 99,
				fontSize: 11,
				fontWeight: 700,
				color: e.color,
				backgroundColor: e.bg,
				whiteSpace: "nowrap",
			}}
		>
			● {label || DEP_STATES[etat] || "—"}
		</span>
	)
}

export const VTag = ({ children }) => (
	<span
		style={{
			fontSize: 10.5,
			fontWeight: 700,
			color: COLORS.accent,
			border: "1px solid #BFDBFE",
			borderRadius: 4,
			padding: "0 5px",
		}}
	>
		{children}
	</span>
)

export const Mono = ({ children }) => (
	<span
		style={{
			fontFamily: 'ui-monospace, "SFMono-Regular", Menlo, monospace',
			fontSize: 12.5,
			fontWeight: 600,
		}}
	>
		{children}
	</span>
)

export const BandeauOmbre = ({ court = false, style = null }) => (
	<div style={{ ...ui.ombre, ...style }}>
		<FontAwesomeIcon icon={faAdjust} />
		<span>
			<b>Mode ombre</b> —{" "}
			{/* AS1 (CDC 16.10) : seules les créations / mises à jour /
			    annulations de MISSION proposées sont bloquées
			    (application.OPS_MISSION) ; tout ce qu'on valide à la main
			    s'écrit réellement. */}
			{court
				? "rien n'est appliqué automatiquement ; vos actions manuelles s'appliquent réellement."
				: "rien n'est appliqué automatiquement : les missions viennent encore de l'ancien import et les créations ou modifications de mission proposées ne s'appliquent pas. Les actions que vous validez à la main s'appliquent réellement (couvertures, liaisons, fusions, rattachements, « Hors sujet »)."}
		</span>
	</div>
)

export const Pastille = ({ n, sourde = false }) =>
	n ? (
		<span
			style={{
				minWidth: 20,
				height: 20,
				padding: "0 6px",
				borderRadius: 99,
				fontSize: 11,
				fontWeight: 700,
				display: "inline-flex",
				alignItems: "center",
				justifyContent: "center",
				boxSizing: "border-box",
				backgroundColor: sourde ? COLORS.border : COLORS.danger,
				color: sourde ? COLORS.textMuted : "#fff",
			}}
		>
			{n}
		</span>
	) : null

// Dates : « 23.09 08:21 » (année omise si courante).
export const dateCourte = (iso) => {
	if (!iso) return ""
	const d = new Date(iso)
	if (Number.isNaN(d.getTime())) return ""
	const p = (n) => String(n).padStart(2, "0")
	const annee =
		d.getFullYear() !== new Date().getFullYear()
			? `.${String(d.getFullYear()).slice(2)}`
			: ""
	return `${p(d.getDate())}.${p(d.getMonth() + 1)}${annee} ${p(
		d.getHours(),
	)}:${p(d.getMinutes())}`
}

export const montant = (valeur, devise) =>
	valeur === null || valeur === undefined
		? "—"
		: `${Number(valeur).toFixed(2)}${devise && devise !== "CHF" ? ` ${devise}` : ".–"}`

export const LIBELLES_CHAMPS = {
	total_presume: "Montant accordé",
	destination_finale_0: "Destination (rue)",
	destination_finale_1: "Destination (NPA localité)",
	destination_finale_2: "Destination (pays)",
	lieu_intervention_0: "Lieu (rue)",
	lieu_intervention_1: "Lieu (NPA localité)",
	lieu_intervention_2: "Lieu (pays)",
	nom: "Nom",
	prenom: "Prénom",
	telephone: "Téléphone",
	plaques: "Plaques",
	marque: "Marque",
	modele: "Modèle",
}

export const ui = {
	ombre: {
		display: "flex",
		alignItems: "center",
		gap: 10,
		padding: "10px 14px",
		borderRadius: 10,
		backgroundColor: COLORS.warningLight,
		border: "1px solid #FDE68A",
		color: "#92400E",
		fontSize: 13,
		marginBottom: 14,
	},
	btn: (variante = "") => ({
		borderRadius: 8,
		padding: "7px 14px",
		fontSize: 13,
		fontWeight: 500,
		cursor: "pointer",
		fontFamily: "inherit",
		whiteSpace: "nowrap",
		border: `1px solid ${
			variante === "primary"
				? COLORS.text
				: variante === "danger"
					? COLORS.dangerBorder
					: variante === "ghost"
						? "transparent"
						: COLORS.border
		}`,
		backgroundColor: variante === "primary" ? COLORS.text : COLORS.surface,
		color:
			variante === "primary"
				? "#fff"
				: variante === "danger"
					? COLORS.danger
					: variante === "ghost"
						? COLORS.textMuted
						: COLORS.text,
	}),
	lien: {
		color: COLORS.accent,
		cursor: "pointer",
		fontWeight: 500,
		fontSize: 12.5,
		background: "none",
		border: 0,
		padding: 0,
		fontFamily: "inherit",
	},
	carte: {
		backgroundColor: COLORS.surface,
		border: `1px solid ${COLORS.border}`,
		borderRadius: 16,
		padding: "16px 18px",
	},
	carteTitre: {
		fontSize: 11,
		fontWeight: 600,
		color: COLORS.textLight,
		letterSpacing: "0.1em",
		textTransform: "uppercase",
		paddingBottom: 10,
		marginBottom: 12,
		borderBottom: `1px solid ${COLORS.border}`,
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		gap: 10,
	},
	indice: {
		textTransform: "none",
		letterSpacing: 0,
		fontWeight: 500,
		color: COLORS.textMuted,
		fontSize: 12,
	},
	plaque: {
		display: "inline-block",
		padding: "0 6px",
		marginRight: 6,
		border: `1.5px solid ${COLORS.text}`,
		borderRadius: 4,
		fontSize: 11,
		fontWeight: 800,
		lineHeight: "17px",
		letterSpacing: "0.06em",
		textTransform: "uppercase",
		whiteSpace: "nowrap",
	},
}

// Plaques interchangeables (Jordan 09.10) : même plaque, autre marque — deux
// véhicules, rien n'est rapproché ; on le signale seulement.
export const texteInterchangeable = (x) =>
	`Même plaque que M${x.mission} (${x.marque})${
		x.meme_client ? ", même client" : ""
	}${x.meme_lieu ? ", même lieu" : ""} — plaques interchangeables ?`
